import { CloseCircleOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
	Button,
	Col,
	Divider,
	Form,
	Image,
	Input,
	message,
	Modal,
	notification,
	Row,
	Select,
	Switch,
	Tabs,
	Tag,
	Upload,
	Typography,
	Space,
} from 'antd';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import uploadApi from '../../api/file';
import Constant from '../../lib/constant';
import pagesApi from '../../api/page';

const { Text } = Typography;

const TEMPLATE_OPTIONS = [
	{ label: 'Trang dịch vụ', value: 'SERVICE' },
	{ label: 'Trang dự án', value: 'PROJECT' },
	{ label: 'Danh sách tin tức', value: 'NEWS_LIST' },
	{ label: 'Danh sách sản phẩm', value: 'PRODUCT_LIST' },
	{ label: 'Nhóm sản phẩm', value: 'PRODUCT_GROUP' },
];

const defaultData = {
	names: { vi: '', en: '' },
	createdAt: new Date(),
	descriptions: { vi: '', en: '' },
	path: '',
	bannerImageUrl: '',
	bannerVideoUrl: '',
	isHeaderVideoPage: false,
	enabled: true,
	template: '',
};

function TV_page_detail(props, ref) {
	const { getProducts } = props;

	const [open, setOpen] = useState(false);
	const [index, setIndex] = useState(defaultData);
	const [initValue, setInitValue] = useState(defaultData);

	const [form] = Form.useForm();
	const refMode = useRef();

	const [api, contextHolder] = notification.useNotification();
	const [loading, setLoading] = useState(false);

	const [errors, setErrors] = useState({});
	const [uploadingBannerImage, setUploadingBannerImage] = useState(false);
	const [uploadingBannerVideo, setUploadingBannerVideo] = useState(false);

	const propsErrors = {
		namesvi: null,
		namesen: null,
		path: null,
		template: null,
	};

	const openNotificationWithIcon = (type, messageText, description) => {
		api[type]({ message: messageText, description });
	};

	const setFieldError = (key, val = null) => setErrors((prev) => ({ ...prev, [key]: val }));
	const getValidateStatus = (key) => (errors?.[key] ? 'error' : undefined);
	const getHelp = (key) => errors?.[key] || undefined;

	useImperativeHandle(ref, () => ({
		create: () => {
			setOpen(true);
			refMode.current = Constant.crud.create;
			setInitValue(defaultData);
			setIndex(defaultData);
			setErrors(propsErrors);
			form.resetFields();
		},
		update: (product) => {
			const { contents, ...remain } = product;

			setInitValue({
				...defaultData,
				...remain,
				isHeaderVideoPage: remain?.isHeaderVideoPage ?? false,
				contents: contents ? contents : { vi: '', en: '' },
			});

			setIndex({
				...defaultData,
				...product,
				isHeaderVideoPage: product?.isHeaderVideoPage ?? false,
				contents: contents ? contents : { vi: '', en: '' },
			});

			setOpen(true);
			refMode.current = Constant.crud.update;
			setErrors(propsErrors);
			form.resetFields();
		},
	}));

	useEffect(() => {
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const validate = (propsToValidate, data) => {
		const _data = structuredClone(data || { ...initValue, contents: index.contents, descriptions: index.descriptions });
		const _errors = structuredClone(errors);

		if (!propsToValidate) propsToValidate = propsErrors;

		Object.keys(propsToValidate).forEach((prop) => {
			switch (prop) {
				case 'namesvi':
					_errors[prop] = _data?.names?.vi?.trim()?.length > 0 ? null : 'Vui lòng nhập tên trang (VI)';
					break;
				case 'namesen':
					_errors[prop] = _data?.names?.en?.trim()?.length > 0 ? null : 'Vui lòng nhập tên trang (EN)';
					break;
				default:
					_errors[prop] = _data[prop]?.toString()?.trim()?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
			}
		});

		setErrors(_errors);
		return Object.values(_errors);
	};

	const handleSubmit = async () => {
		setInitValue({ ...initValue, descriptions: index.descriptions });

		try {
			const _errorList = validate();
			if (_errorList.filter((x) => x).length === 0) {
				setLoading(true);

				if (refMode.current === Constant.crud.update) {
					const { _id, ...remain } = initValue;

					await pagesApi.update(_id, {
						...remain,
						descriptions: index.descriptions,
					});
				} else {
					await pagesApi.create({
						...initValue,
						descriptions: index.descriptions,
					});
				}

				openNotificationWithIcon('success', 'Thành công', 'Thao tác thành công');
				onCancel();
				getProducts();
			}
		} catch (err) {
			console.log(err);
			message.error('Có lỗi xảy ra, vui lòng thử lại.');
		} finally {
			setLoading(false);
		}
	};

	const onCancel = () => {
		setOpen(false);
		refMode.current = '';
		form.resetFields();
		setInitValue(defaultData);
		setIndex(defaultData);
		setErrors(propsErrors);
	};

	// ===== CKEditor upload =====
	function uploadPlugin(editor) {
		editor.plugins.get('FileRepository').createUploadAdapter = (loader) => uploadAdapter(loader);
	}

	function uploadAdapter(loader) {
		return {
			upload: () =>
				new Promise(async (resolve, reject) => {
					try {
						const file = await loader.file;
						if (!file) return resolve({ default: '' });

						uploadApi
							.uploadImage(file)
							.then((data) => {
								resolve({ default: data.data.data.url });
								message.success('Upload thành công');
							})
							.catch(() => message.error('Upload thất bại'));
					} catch (e) {
						reject('Error');
					}
				}),
			abort: () => {},
		};
	}

	const pickUploadedUrl = (res) => res?.data?.data?.url || res?.data?.data?.data?.url || '';

	const isVideoHeader = !!initValue.isHeaderVideoPage;
	const disableImage = isVideoHeader; // header dùng video => khóa image banner
	const disableVideo = !isVideoHeader; // header dùng image => khóa video banner

	const bannerImageUploadProps = {
		accept: 'image/*',
		showUploadList: false,
		disabled: disableImage,
		customRequest: async ({ file, onSuccess, onError }) => {
			try {
				setUploadingBannerImage(true);
				const res = await uploadApi.uploadImage(file);
				const url = pickUploadedUrl(res);
				if (!url) throw new Error('Missing url');

				setInitValue((prev) => ({ ...prev, bannerImageUrl: url }));
				message.success('Đã upload banner ảnh');
				onSuccess?.(res.data);
			} catch (e) {
				console.log(e);
				message.error('Upload banner ảnh thất bại');
				onError?.(e);
			} finally {
				setUploadingBannerImage(false);
			}
		},
	};

	const bannerVideoUploadProps = {
		accept: 'video/*',
		showUploadList: false,
		disabled: disableVideo,
		customRequest: async ({ file, onSuccess, onError }) => {
			try {
				setUploadingBannerVideo(true);
				const res = await uploadApi.uploadVideo(file);
				const url = pickUploadedUrl(res);
				if (!url) throw new Error('Missing url');

				setInitValue((prev) => ({ ...prev, bannerVideoUrl: url }));
				message.success('Đã upload banner video');
				onSuccess?.(res.data);
			} catch (e) {
				console.log(e);
				message.error('Upload banner video thất bại');
				onError?.(e);
			} finally {
				setUploadingBannerVideo(false);
			}
		},
	};

	return (
		<>
			{contextHolder}

			<Modal
				width="86%"
				open={open}
				onCancel={onCancel}
				centered
				destroyOnHidden
				title={
					<Space size={8}>
						<span>{refMode.current === Constant.crud.create ? 'Tạo mới trang' : 'Cập nhật trang'}</span>
						<Tag color={isVideoHeader ? 'blue' : 'green'}>
							{isVideoHeader ? 'Header: Video' : 'Header: Image'}
						</Tag>
					</Space>
				}
				footer={[
					<Button
						icon={<CloseCircleOutlined />}
						key="cancel"
						type="default"
						danger
						disabled={loading}
						onClick={onCancel}
					>
						Hủy
					</Button>,
					<Button
						icon={<SaveOutlined />}
						key="submit"
						type="primary"
						loading={loading}
						onClick={handleSubmit}
					>
						{refMode.current === Constant.crud.create ? 'Tạo mới' : 'Cập nhật'}
					</Button>,
				]}
			>
				<Form form={form} layout="vertical">
					{/* ===== BASIC INFO ===== */}
					<Row gutter={[12, 12]}>
						<Col span={12}>
							<Form.Item
								label="Tên trang (Tiếng Việt)"
								validateStatus={getValidateStatus('namesvi')}
								help={getHelp('namesvi')}
							>
								<Input
									placeholder="VD: Giới thiệu"
									value={initValue.names?.vi}
									onChange={(e) => {
										setInitValue((prev) => ({
											...prev,
											names: { vi: e.target.value, en: prev.names.en },
										}));
										setFieldError('namesvi', null);
									}}
								/>
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item
								label="Tên trang (Tiếng Anh)"
								validateStatus={getValidateStatus('namesen')}
								help={getHelp('namesen')}
							>
								<Input
									placeholder="VD: About us"
									value={initValue.names?.en}
									onChange={(e) => {
										setInitValue((prev) => ({
											...prev,
											names: { en: e.target.value, vi: prev.names.vi },
										}));
										setFieldError('namesen', null);
									}}
								/>
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item label="Hoạt động">
								<Switch
									checked={!!initValue.enabled}
									onChange={(val) => setInitValue((prev) => ({ ...prev, enabled: val }))}
								/>
								<Text type="secondary" style={{ marginLeft: 10 }}>
									{initValue.enabled ? 'Đang bật' : 'Đang tắt'}
								</Text>
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item label="Header dùng video?">
								<Switch
									checked={!!initValue.isHeaderVideoPage}
									onChange={(val) => {
										setInitValue((prev) => ({ ...prev, isHeaderVideoPage: val }));
									}}
								/>
								<Text type="secondary" style={{ marginLeft: 10 }}>
									{isVideoHeader ? 'Ưu tiên banner video' : 'Ưu tiên banner ảnh'}
								</Text>
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item
								label="Path"
								validateStatus={getValidateStatus('path')}
								help={getHelp('path') || <span>Ví dụ: <code>/page/a-b-c</code></span>}
							>
								<Input
									placeholder="/page/slug"
									value={initValue.path}
									onChange={(e) => {
										setInitValue((prev) => ({ ...prev, path: e.target.value }));
										setFieldError('path', null);
									}}
								/>
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item
								label="Template"
								validateStatus={getValidateStatus('template')}
								help={getHelp('template')}
							>
								<Select
									placeholder="Chọn template"
									value={initValue.template || undefined}
									allowClear
									options={TEMPLATE_OPTIONS}
									onChange={(val) => {
										setInitValue((prev) => ({ ...prev, template: val || '' }));
										setFieldError('template', null);
									}}
								/>
							</Form.Item>
						</Col>
					</Row>

					<Divider style={{ margin: '10px 0 14px' }}>Banner</Divider>

					{/* ===== BANNER ===== */}
					<Row gutter={[12, 12]}>
						<Col span={12}>
							<Form.Item
								label={
									<Space size={8}>
										<span>Banner Image Url</span>
										{disableImage ? <Tag color="default">Đang khóa (Header = Video)</Tag> : null}
									</Space>
								}
							>
								<Input
									disabled={disableImage}
									placeholder="Dán link ảnh hoặc upload"
									value={initValue.bannerImageUrl}
									onChange={(e) => setInitValue((prev) => ({ ...prev, bannerImageUrl: e.target.value }))}
									addonAfter={
										<Upload {...bannerImageUploadProps}>
											<Button
												type="text"
												icon={<UploadOutlined />}
												loading={uploadingBannerImage}
												disabled={disableImage}
											>
												Upload
											</Button>
										</Upload>
									}
								/>

								{initValue.bannerImageUrl ? (
									<div style={{ marginTop: 10 }}>
										<Image
											src={initValue.bannerImageUrl}
											alt="banner"
											width={220}
											style={{ borderRadius: 12 }}
											preview
										/>
									</div>
								) : (
									<Text type="secondary">Chưa có ảnh banner.</Text>
								)}
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item
								label={
									<Space size={8}>
										<span>Banner Video Url</span>
										{disableVideo ? <Tag color="default">Đang khóa (Header = Image)</Tag> : null}
									</Space>
								}
							>
								<Input
									disabled={disableVideo}
									placeholder="Dán link video hoặc upload"
									value={initValue.bannerVideoUrl}
									onChange={(e) => setInitValue((prev) => ({ ...prev, bannerVideoUrl: e.target.value }))}
									addonAfter={
										<Upload {...bannerVideoUploadProps}>
											<Button
												type="text"
												icon={<UploadOutlined />}
												loading={uploadingBannerVideo}
												disabled={disableVideo}
											>
												Upload
											</Button>
										</Upload>
									}
								/>

								{initValue.bannerVideoUrl ? (
									<div style={{ marginTop: 10 }}>
										<video
											src={initValue.bannerVideoUrl}
											controls
											style={{
												width: '100%',
												maxHeight: 240,
												borderRadius: 12,
												background: '#000',
											}}
										/>
									</div>
								) : (
									<Text type="secondary">Chưa có video banner.</Text>
								)}
							</Form.Item>
						</Col>
					</Row>

					<Divider style={{ margin: '10px 0 14px' }}>Mô tả</Divider>

					{/* ===== DESCRIPTIONS ===== */}
					<Tabs
						defaultActiveKey="vi"
						items={[
							{
								key: 'vi',
								label: 'Tiếng Việt',
								children: (
									<Form.Item
										label={
											<Space size={8}>
												<span>Mô tả (VI)</span>
												<Tag color="default">Không bắt buộc</Tag>
											</Space>
										}
									>
										<CKEditor
											editor={ClassicEditor}
											data={index?.descriptions?.vi || ''}
											config={{ extraPlugins: [uploadPlugin] }}
											onChange={(event, editor) => {
												const data = editor.getData();
												setIndex((prev) => ({
													...prev,
													descriptions: { vi: data, en: prev.descriptions.en },
												}));
											}}
										/>
										<Text type="secondary">Bạn có thể để trống phần mô tả.</Text>
									</Form.Item>
								),
							},
							{
								key: 'en',
								label: 'English',
								children: (
									<Form.Item
										label={
											<Space size={8}>
												<span>Description (EN)</span>
												<Tag color="default">Optional</Tag>
											</Space>
										}
									>
										<CKEditor
											editor={ClassicEditor}
											data={index?.descriptions?.en || ''}
											config={{ extraPlugins: [uploadPlugin] }}
											onChange={(event, editor) => {
												const data = editor.getData();
												setIndex((prev) => ({
													...prev,
													descriptions: { en: data, vi: prev.descriptions.vi },
												}));
											}}
										/>
										<Text type="secondary">You can leave this empty.</Text>
									</Form.Item>
								),
							},
						]}
					/>
				</Form>
			</Modal>
		</>
	);
}

export default forwardRef(TV_page_detail);
